/**
 * Profile Service
 *
 * Fetches user profile data using SteemWorld SDK and dsteem.
 */

import { UserProfile } from '@/types/user'
import { getAccount } from '@/services/steemit/authService'
import { getFollowCounters } from '@/services/steemworld'

interface ProfileMetadata {
  profile?: {
    name?: string
    about?: string
    location?: string
    website?: string
    profile_image?: string
    cover_image?: string
  }
}

/**
 * Calculate reputation score from raw reputation value
 */
function calculateReputation(rawReputation: string | number): number {
  const rep = typeof rawReputation === 'string' ? parseInt(rawReputation, 10) : rawReputation
  if (rep === 0) return 25
  const neg = rep < 0
  const out = Math.log10(Math.abs(rep))
  let reputation = Math.max(out - 9, 0) * 9 + 25
  if (neg) reputation = 50 - reputation
  return Math.floor(reputation)
}

/**
 * Parse profile metadata from JSON
 */
function parseProfileMetadata(jsonMetadata: string): ProfileMetadata {
  try {
    if (!jsonMetadata) return {}
    return JSON.parse(jsonMetadata)
  } catch {
    return {}
  }
}

/**
 * Fetches complete user profile
 */
export async function getUserProfile(username: string): Promise<UserProfile | null> {
  try {
    const [account, followCounts] = await Promise.all([
      getAccount(username),
      getFollowCounters(username),
    ])

    if (!account) {
      return null
    }

    // Parse metadata from posting_json_metadata first, fallback to json_metadata
    const metadata = parseProfileMetadata(account.posting_json_metadata) ||
                     parseProfileMetadata(account.json_metadata)
    const profile = metadata.profile || {}

    return {
      username: account.name,
      reputation: calculateReputation(account.reputation),
      created: new Date(account.created),
      profileImage: profile.profile_image || null,
      coverImage: profile.cover_image || null,
      about: profile.about || null,
      location: profile.location || null,
      website: profile.website || null,
      followerCount: followCounts.followers,
      followingCount: followCounts.following,
      postCount: account.post_count,
      lastActive: null,
    }
  } catch (error) {
    console.error('Failed to fetch user profile:', error)
    return null
  }
}
